import React, { useState } from 'react';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, DataTable, EmptyState, ErrorState, FeedbackBanner, LoadingState, MvpCard, MvpTalentLayout, StatusBadge } from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const NotificationsPage: React.FC = () => {
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [retryTarget, setRetryTarget] = useState<number | null>(null);
  const { data, loading, error, reload } = useAsyncData((signal) => platformCoreNetworkApi.listNotificationDeliveries({ failed: true, page: 1, limit: 20 }, { signal }), []);

  return (
    <MvpTalentLayout title="Notification Operations" subtitle="Delivery failures, queue visibility, and safe retry operations.">
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      {loading ? <LoadingState label="Loading notification deliveries…" /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <MvpCard title="Notification deliveries">
          {data.rows.length ? (
            <DataTable columns={['Delivery', 'Type', 'Recipient', 'Status', 'Last error', 'Created', 'Actions']}>
              {data.rows.map((delivery) => (
                <tr key={delivery.id}>
                  <td>#{delivery.id}</td>
                  <td>{delivery.notificationType}</td>
                  <td>{delivery.recipientEmail || delivery.recipientMemberId || '—'}</td>
                  <td><StatusBadge status={delivery.status} /></td>
                  <td>{delivery.errorMessage || '—'}</td>
                  <td>{formatDateTime(delivery.createdAt)}</td>
                  <td><button type="button" className="mvp-button mvp-button--ghost" onClick={() => setRetryTarget(delivery.id)}>Retry</button></td>
                </tr>
              ))}
            </DataTable>
          ) : <EmptyState title="No failed deliveries" message="The platform API did not return any failed notification deliveries." />}
        </MvpCard>
      ) : null}
      <ConfirmDialog
        open={retryTarget != null}
        title="Retry notification delivery"
        description="This schedules an idempotent retry through the protected platform API."
        confirmLabel="Retry delivery"
        onClose={() => setRetryTarget(null)}
        onConfirm={async ({ reason }) => {
          if (retryTarget == null) return;
          try {
            await platformCoreNetworkApi.retryNotificationDelivery(retryTarget, reason);
            setFeedback({ tone: 'success', message: `Delivery #${retryTarget} queued for retry.` });
            await reload();
          } catch (retryError: any) {
            setFeedback({ tone: 'error', message: retryError?.message || 'Unable to retry the notification delivery.' });
            throw retryError;
          }
        }}
      />
    </MvpTalentLayout>
  );
};

export default NotificationsPage;
